<script>
import Overlay from '../../mixins/overlay.js'
import Tool from '../../mixins/tool.js'
import Pin from '../primitives/pin.js'
import { scanPatterns } from '../../workspace/pattern-scanner.mjs'
import { patternSymbol, drawPatternSymbol, patternGuides, layoutPatternBadges } from '../../workspace/pattern-symbols.mjs'
export default {
    name: 'AutoPatternTool',
    mixins: [Overlay, Tool],
    props: ['patternCandles','drawingPrefs'],
    computed: {
        analysis() { return scanPatterns(this.patternCandles, this.settings.p1, this.settings.p2, this.settings) },
    },
    methods: {
        meta_info() { return { author:'TradingCandle', version:'1.0.0' } },
        use_for() { return ['AutoPatternTool'] },
        tool() { return { group:'Patterns', category:'Patterns', name:'Auto Pattern Box', type:'AutoPatternTool',
            icon:'rectangle.svg', hint:'Select a box to identify candlestick and chart pattern candidates', data:[], settings:{} } },
        init() {
            this.pins.push(new Pin(this,'p1'))
            const pin=new Pin(this,'p2',{state:'tracking'})
            this.pins.push(pin)
            pin.on('settled',()=>{
                this.set_state('finished')
                this.$emit('drawing-mode-off')
                this.$emit('scroll-lock',false)
                this.$emit('object-selected',[this.$parent,this.type,this.settings.$uuid])
            })
            this.keys.on('Escape',()=>{
                if(this.state!=='finished') {
                    this.$emit('remove-tool'); this.$emit('drawing-mode-off'); this.$emit('scroll-lock',false)
                }
            })
        },
        draw(ctx) {
            if(this.drawingPrefs?.hidden)return
            const {p1,p2}=this.settings
            if(!p1 || !p2)return
            const l=this.layout, xy=p=>[l.t2screen(p[0]),l.$2screen(p[1])]
            const a=xy(p1),b=xy(p2),x=Math.min(a[0],b[0]),y=Math.min(a[1],b[1]),w=Math.abs(a[0]-b[0]),h=Math.abs(a[1]-b[1])
            this.collisions.push((px,py)=>px>=x-6 && px<=x+w+6 && py>=y-6 && py<=y+h+6 && Math.min(Math.abs(px-x),Math.abs(px-x-w),Math.abs(py-y),Math.abs(py-y-h))<6)
            ctx.save()
            ctx.globalAlpha=(this.settings.opacity??100)/100
            ctx.strokeStyle=this.settings.color || '#7c3aed'; ctx.lineWidth=this.settings.lineWidth || 1.5
            if(this.settings.fill!==false){ctx.fillStyle=this.settings.backColor||'rgba(124,58,237,0.06)';ctx.fillRect(x,y,w,h)}
            ctx.strokeRect(x,y,w,h)
            const result=this.analysis
            const patterns=result.patterns.filter(pattern=>!this.settings.patternFamily||this.settings.patternFamily==='all'||pattern.kind===this.settings.patternFamily)
            const colorFor=definition=>definition?.bias==='bullish'?'#26a69a':definition?.bias==='bearish'?'#ef5350':'#a78bfa'
            ctx.font='11px sans-serif';ctx.textBaseline='top'
            // Clip the formation outlines to the selected scan region. Badges
            // can use nearby chart space rather than being cropped by the box.
            ctx.save();ctx.beginPath();ctx.rect(x,y,w,h);ctx.clip()
            for(const pattern of patterns){
                const definition=patternSymbol(pattern),color=colorFor(definition),guides=patternGuides(pattern)
                if(this.settings.showPatternOutlines!==false){
                    for(const path of guides.paths){
                        ctx.strokeStyle=color;ctx.lineWidth=path.role==='swings'?1:2
                        ctx.globalAlpha=(this.settings.opacity??100)/100*(path.role==='swings'?.35:.9)
                        ctx.setLineDash(path.role==='neckline'?[5,4]:path.role==='swings'?[2,3]:[])
                        ctx.beginPath();path.points.map(xy).forEach((point,index)=>index?ctx.lineTo(...point):ctx.moveTo(...point));ctx.stroke()
                    }
                    if(pattern.kind==='candlestick'){
                        ctx.setLineDash([]);ctx.strokeStyle=color;ctx.lineWidth=1.5;ctx.globalAlpha=(this.settings.opacity??100)/100*.7
                        const step=l.px_step||Math.abs(l.t2screen((pattern.candles.at(-1)?.[0]||0)+Number(this.interval))-l.t2screen(pattern.candles.at(-1)?.[0]||0))||6
                        const bodyWidth=Math.max(3,Math.min(14,step*.8))
                        for(const candle of pattern.candles){
                            const px=l.t2screen(candle[0]),top=l.$2screen(Math.max(candle[1],candle[4])),bottom=l.$2screen(Math.min(candle[1],candle[4]))
                            ctx.beginPath();ctx.moveTo(px,l.$2screen(candle[2]));ctx.lineTo(px,l.$2screen(candle[3]));ctx.stroke()
                            ctx.strokeRect(px-bodyWidth/2,top-1,bodyWidth,Math.max(2,bottom-top+2))
                        }
                    }
                }
                if(this.settings.showLabels!==false){
                    ctx.setLineDash([]);ctx.fillStyle=color;ctx.globalAlpha=(this.settings.opacity??100)/100
                    for(const label of guides.labels){const point=xy(label.point);ctx.fillText(label.text,point[0]+3,point[1]-14)}
                }
            }
            ctx.restore();ctx.setLineDash([])
            const size=Math.max(16,Math.min(30,Number(this.settings.patternSymbolSize)||22))
            const items=patterns.map(pattern=>{
                const definition=patternSymbol(pattern),last=pattern.candles.at(-1)
                const anchor=pattern.kind==='candlestick'?xy([last[0],definition?.bias==='bullish'?last[3]:last[2]]):xy(pattern.points.at(-1))
                const label=this.settings.showLabels!==false&&(pattern.kind==='chart'||this.settings.detailedPatternLabels)?(definition?.label||pattern.name):''
                return {pattern,definition,anchor,label,bias:definition?.bias,width:size+10+(label?ctx.measureText(label).width+5:0),height:size+6}
            }).sort((a,b)=>(a.pattern.kind==='chart'?0:1)-(b.pattern.kind==='chart'?0:1)||a.pattern.end-b.pattern.end)
            const viewport={x:4,y:4,width:Math.max(20,l.width-8),height:Math.max(20,(l.height||y+h+40)-8)}
            let visibleItems=items
            if(this.settings.compactPatternBadges!==false){
                const groups=new Map()
                for(const item of items){
                    const key=item.pattern.kind==='chart'?item.pattern.name+item.pattern.start+':'+item.pattern.end:'candle:'+Math.floor(item.anchor[0]/(size+12))
                    if(!groups.has(key))groups.set(key,[])
                    groups.get(key).push(item)
                }
                visibleItems=[...groups.values()].map(members=>{
                    members.sort((a,b)=>(b.pattern.end-b.pattern.start)-(a.pattern.end-a.pattern.start)||(b.bias==='neutral'?0:1)-(a.bias==='neutral'?0:1))
                    const first=members[0],label=first.label+(members.length>1?' +'+(members.length-1):'')
                    return {...first,members,label,width:size+10+(label?ctx.measureText(label).width+5:0)}
                })
            }
            const badges=layoutPatternBadges(visibleItems,viewport,[{x,y,width:Math.min(w,260),height:24}])
            let hovered=null
            for(const badge of badges){
                const color=colorFor(badge.definition),inside=this.mouse&&this.mouse.x>=badge.x&&this.mouse.x<=badge.x+badge.width&&this.mouse.y>=badge.y&&this.mouse.y<=badge.y+badge.height
                if(inside)hovered=badge
                ctx.globalAlpha=(this.settings.opacity??100)/100
                ctx.strokeStyle=color;ctx.lineWidth=1;ctx.beginPath();ctx.moveTo(...badge.anchor);ctx.lineTo(Math.max(badge.x,Math.min(badge.anchor[0],badge.x+badge.width)),Math.max(badge.y,Math.min(badge.anchor[1],badge.y+badge.height)));ctx.stroke()
                ctx.fillStyle=this.colors.back||'#171b26';ctx.fillRect(badge.x,badge.y,badge.width,badge.height)
                ctx.strokeStyle=color;ctx.lineWidth=inside?2:1;ctx.strokeRect(badge.x+.5,badge.y+.5,badge.width-1,badge.height-1)
                ctx.fillStyle=ctx.strokeStyle=color
                drawPatternSymbol(ctx,badge.x+size/2+4,badge.y+badge.height/2,size,badge.definition)
                if(badge.label)ctx.fillText(badge.label,badge.x+size+8,badge.y+(badge.height-11)/2)
            }
            ctx.globalAlpha=(this.settings.opacity??100)/100
            ctx.fillStyle=this.colors.back||'#171b26';ctx.fillRect(x+1,y+1,Math.min(w-2,260),20)
            ctx.fillStyle=this.colors.text||'#ddd';ctx.fillText(`${result.candles} candles · ${patterns.length} pattern candidates`,x+6,y+5)
            if(!patterns.length)ctx.fillText('No supported patterns found',x+6,y+23)
            if(hovered){
                const members=hovered.members||[hovered],rowHeight=24,maxRows=Math.max(1,Math.floor(((l.height||300)-36)/rowHeight))
                const shown=members.slice(0,maxRows),remaining=members.length-shown.length
                const width=Math.min(l.width-8,Math.max(...shown.map(item=>ctx.measureText(item.pattern.name).width))+44),height=(shown.length+(remaining?1:0))*rowHeight+8
                const tx=Math.max(4,Math.min(hovered.x,l.width-width-4)),ty=Math.max(4,Math.min(hovered.y-height-4,(l.height||300)-height-4))
                ctx.fillStyle=this.colors.back||'#171b26';ctx.fillRect(tx,ty,width,height);ctx.strokeStyle=colorFor(hovered.definition);ctx.strokeRect(tx+.5,ty+.5,width-1,height-1)
                shown.forEach((item,index)=>{
                    ctx.fillStyle=ctx.strokeStyle=colorFor(item.definition);drawPatternSymbol(ctx,tx+15,ty+16+index*rowHeight,20,item.definition)
                    ctx.fillText(item.pattern.name,tx+32,ty+10+index*rowHeight)
                })
                if(remaining){ctx.fillStyle=this.colors.text||'#ddd';ctx.fillText('+'+remaining+' candidates · select patterns in settings',tx+6,ty+10+shown.length*rowHeight)}
            }
            ctx.restore();this.render_pins(ctx)
        },
        data_colors() { return [this.settings.color || '#7c3aed'] },
    },
}
</script>
